import { Ref, useState, useCallback, useRef } from 'react';

import { useResizeObserver } from './use-resize-observer';

interface UseBoundingClientRectParams {
    /** Default: false */
    recalcOnResize?: boolean;
}

export const useBoundingClientRect = <T extends HTMLElement>(
    params: UseBoundingClientRectParams = {}
): [DOMRect | null, Ref<T>, () => DOMRect | null] => {
    const { recalcOnResize = false } = params;
    const [elemRect, setElemRect] = useState<DOMRect | null>(null);
    const elemRef = useRef<T | null>(null);
    const elemRefCallback = useCallback((node: T | null) => {
        elemRef.current = node;
        if (node) {
            setElemRect(node.getBoundingClientRect());
        } else {
            setElemRect(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const refresh = useCallback(() => {
        const node = elemRef.current;
        if (node) {
            const clientRect = node.getBoundingClientRect();
            setElemRect((prev) => {
                if (!prev) {
                    return clientRect;
                }
                if (
                    prev.top !== clientRect.top ||
                    prev.left !== clientRect.left ||
                    prev.right !== clientRect.right ||
                    prev.bottom !== clientRect.bottom ||
                    prev.width !== clientRect.width ||
                    prev.height !== clientRect.height
                ) {
                    return clientRect;
                }
                return prev;
            });
            return clientRect;
        }
        return null;
    }, []);

    useResizeObserver({
        ref: elemRef,
        onResize: setElemRect,
        enabled: recalcOnResize,
    });

    return [elemRect, elemRefCallback, refresh];
};
